/* UpdateDeviceStatusesVeilRenderer.js */

"use strict";

const uiCore = require("@gluenetworks/steve_ui_core");
const InputRadioControlComponent = uiCore.Control.OptionSelection.Single.InputRadio;
const TextareaControlComponent = uiCore.Control.Textfield.Textarea;
const ButtonControl = uiCore.Control.Action.Button;

class UpdateDeviceStatusesVeilRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("update-device-statuses-veil");
        this._rootElem.component = this._model;

        this._title = document.createElement("h2");
        this._title.textContent = "Modify IoT Device Upgrade Status";

        this._deviceMessage = document.createElement("p");
        this._deviceMessage.classList.add("device-message");
        const deviceCount = this._model.getDeviceCount();

        if (deviceCount === 1) {
            this._deviceMessage.textContent = `Set upgrade status of the selected device`;
        } else {
            this._deviceMessage.textContent = `Set upgrade status of ${deviceCount} selected devices`;
        }
        this._statusRadioControl = new InputRadioControlComponent({
            options: [
                {label: "PENDING", value: "PENDING"},
                {label: "UPGRADED", value: "UPGRADED"},
            ],
            value: "PENDING",
            horizontal: true,
            darkTheme: true,
        });

        this._commentTextareaControl = new TextareaControlComponent({
            label: "Add a Comment:",
            placeholder: "Enter your comment here...",
            rows: 4,
            darkTheme: true,
        });

        this._commentTextAreaLabel = document.createElement("label");
        this._commentTextAreaLabel.textContent = "Add a Comment:";
        this._commentTextAreaLabel.classList.add("comment-textarea-label");

        this._reuseLastEntryButton = new ButtonControl({
            label: "Reuse Last Entry",
            darkTheme: true,
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._confirmButton = new ButtonControl({
            label: "Confirm",
            darkTheme: true,
            theme: "additive",
        });

        this._buttonContainer = document.createElement("div");
        this._buttonContainer.className = "button-container";

        this._rightButtonGroup = document.createElement("div");
        this._rightButtonGroup.className = "right-button-group";

        this.render();

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /*******************************************/

    _shutdown() {
        return new Promise((resolve) => {
            this._unregisterListeners();

            if (this._statusRadioControl) {
                this._statusRadioControl.shutdown();
                this._statusRadioControl = undefined;
            }

            if (this._commentTextareaControl) {
                this._commentTextareaControl.shutdown();
                this._commentTextareaControl = undefined;
            }

            if (this._reuseLastEntryButton) {
                this._reuseLastEntryButton.shutdown();
                this._reuseLastEntryButton = undefined;
            }

            if (this._cancelButton) {
                this._cancelButton.shutdown();
                this._cancelButton = undefined;
            }

            if (this._confirmButton) {
                this._confirmButton.shutdown();
                this._confirmButton = undefined;
            }

            this._title.remove();
            this._title = undefined;
            this._deviceMessage.remove();
            this._deviceMessage = undefined;
            this._rightButtonGroup.remove();
            this._rightButtonGroup = undefined;
            this._buttonContainer.remove();
            this._buttonContainer = undefined;

            this._rootElem.remove();
            this._rootElem = undefined;
            this._model = undefined;
            resolve();
        });
    }

    _registerListeners() {
        this._onStatusChange = this._onStatusChange.bind(this);
        this._onCommentChange = this._onCommentChange.bind(this);
        this._onReuseLastEntryClick = this._onReuseLastEntryClick.bind(this);
        this._onReuseLastEntry = this._onReuseLastEntry.bind(this);
        this._onCancelClick = this._onCancelClick.bind(this);
        this._onConfirmClick = this._onConfirmClick.bind(this);

        this._statusRadioControl.on("UserValue", this._onStatusChange);
        this._commentTextareaControl.on("UserValue", this._onCommentChange);
        this._reuseLastEntryButton.on("UserClick", this._onReuseLastEntryClick);
        this._model.on("ReuseLastEntry", this._onReuseLastEntry);
        this._cancelButton.on("UserClick", this._onCancelClick);
        this._confirmButton.on("UserClick", this._onConfirmClick);
    }

    _unregisterListeners() {
        if (this._statusRadioControl) {
            this._statusRadioControl.removeAllListeners("UserValue");
        }
        if (this._commentTextareaControl) {
            this._commentTextareaControl.removeAllListeners("UserValue");
        }
        if (this._reuseLastEntryButton) {
            this._reuseLastEntryButton.removeAllListeners("UserClick");
        }
        if (this._model) {
            this._model.removeListener("ReuseLastEntry", this._onReuseLastEntry);
        }
        if (this._cancelButton) {
            this._cancelButton.removeAllListeners("UserClick");
        }
        if (this._confirmButton) {
            this._confirmButton.removeAllListeners("UserClick");
        }
    }

    _onStatusChange(value) {
        this._model.setSelectedStatus(value);
    }

    _onCommentChange(value) {
        this._model.setComment(value);
    }

    _onReuseLastEntryClick() {
        this._model.reuseLastEntry();
    }

    _onReuseLastEntry(data) {
        if (data.status) {
            this._statusRadioControl.setValue(data.status);
        }
        if (data.comment) {
            this._commentTextareaControl.setValue(data.comment);
        }
    }

    _onCancelClick() {
        this._model.cancelAction();
    }

    _onConfirmClick() {
        const data = this._model.getData();
        this._model.sendResponse(data);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._title);
                this._rootElem.appendChild(this._deviceMessage);
                this._rootElem.appendChild(this._statusRadioControl.renderer.rootElement);
                this._rootElem.appendChild(this._commentTextAreaLabel);
                this._rootElem.appendChild(this._commentTextareaControl.renderer.rootElement);
                this._rootElem.appendChild(this._buttonContainer);

                this._buttonContainer.appendChild(this._reuseLastEntryButton.renderer.rootElement);

                this._rightButtonGroup.appendChild(this._cancelButton.renderer.rootElement);
                this._rightButtonGroup.appendChild(this._confirmButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._rightButtonGroup);
            }
            resolve();
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /*******************************************/
}

module.exports = UpdateDeviceStatusesVeilRenderer;
